import React, { useState, useEffect } from 'react';
import { Shield, TrendingDown, Hash, Clock, Search, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface BidStats {
  totalRequests: number;
  activeBids: number;
  avgBidsPerRequest: number;
  avgResponseTime: number;
}

interface Bid {
  id: string;
  request_id: string;
  insurer_id: string;
  amount: number;
  status: string;
  created_at: string;
  insurer_name: string;
}

export function BidsManagement() {
  const [stats, setStats] = useState<BidStats>({
    totalRequests: 0,
    activeBids: 0,
    avgBidsPerRequest: 0,
    avgResponseTime: 0
  });
  const [bids, setBids] = useState<Bid[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchField, setSearchField] = useState<'request' | 'bid'>('request');
  const [selectedBid, setSelectedBid] = useState<Bid | null>(null);
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'withdrawn'>('all');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    fetchBidsAndStats();
  }, []);

  async function fetchBidsAndStats() {
    try {
      setLoading(true);
      setError(null);

      // Fetch bids with insurer details
      const { data: bidsData, error: bidsError } = await supabase
        .from('bids')
        .select(`
          id,
          request_id,
          insurer_id,
          amount,
          status,
          created_at,
          reference_number,
          insurers (
            name
          ),
          insurance_requests (
            name,
            reference_number
          )
        `)
        .order('created_at', { ascending: false })
        .limit(50);

      if (bidsError) throw bidsError;

      // Fetch stats
      const { data: statsData, error: statsError } = await supabase
        .from('insurance_requests')
        .select(`
          id,
          bids (
            id,
            amount,
            status,
            created_at
          )
        `);

      if (statsError) throw statsError;

      // Calculate stats
      const totalRequests = statsData?.length || 0;
      const allBids = statsData?.flatMap(r => r.bids) || [];
      const activeBids = allBids.filter(b => b.status === 'active').length;
      const avgBidsPerRequest = totalRequests > 0 ? allBids.length / totalRequests : 0;

      // Calculate average response time
      const responseTimes = statsData?.map(request => {
        if (!request.bids || request.bids.length === 0) return null;
        const firstBidTime = new Date(request.bids[0].created_at).getTime();
        const requestTime = new Date(request.created_at).getTime();
        return (firstBidTime - requestTime) / (1000 * 60 * 60); // Convert to hours
      }).filter(time => time !== null) as number[];

      const avgResponseTime = responseTimes.length > 0
        ? responseTimes.reduce((a, b) => a + b, 0) / responseTimes.length
        : 0;

      setStats({
        totalRequests,
        activeBids,
        avgBidsPerRequest,
        avgResponseTime
      });

      setBids(bidsData || []);
    } catch (err) {
      console.error('Error fetching bids:', err);
      setError('Failed to load bids data');
    } finally {
      setLoading(false);
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess('');

    try {
      // Validate bid amount
      if (error) throw error;

      // Refresh bids list
      await fetchBids();
      setSuccess('Bid submitted successfully!');
    } catch (err) {
      console.error('Error placing bid:', err);
      setError(err instanceof Error ? err.message : 'Failed to place bid');
    } finally {
      setLoading(false);
    }
  };

  const filteredBids = bids.filter(bid => 
    bid && (searchField === 'request' ? 
      (bid.insurance_requests?.reference_number?.toString() || '').includes(searchTerm) :
      (bid.reference_number?.toString() || '').includes(searchTerm))
    && (statusFilter === 'all' || bid.status === statusFilter)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-indigo-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Stats Overview */}
      <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-indigo-100 rounded">
              <Shield className="h-6 w-6 text-indigo-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Total Requests</h3>
              <p className="text-2xl font-semibold text-gray-900">{stats.totalRequests}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-green-100 rounded">
              <TrendingDown className="h-6 w-6 text-green-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Active Bids</h3>
              <p className="text-2xl font-semibold text-gray-900">{stats.activeBids}</p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-yellow-100 rounded">
              <Hash className="h-6 w-6 text-yellow-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Avg Bids/Request</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.avgBidsPerRequest.toFixed(1)}
              </p>
            </div>
          </div>
        </div>
        <div className="bg-white p-6 rounded-lg shadow">
          <div className="flex items-center">
            <div className="p-2 bg-blue-100 rounded">
              <Clock className="h-6 w-6 text-blue-600" />
            </div>
            <div className="ml-4">
              <h3 className="text-sm font-medium text-gray-500">Avg Response Time</h3>
              <p className="text-2xl font-semibold text-gray-900">
                {stats.avgResponseTime.toFixed(1)}h
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Bids Table */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-4">
            <h3 className="text-lg font-medium text-gray-900">Recent Bids</h3>
            <div className="w-full sm:w-auto flex flex-col sm:flex-row gap-2">
              <Search className="h-5 w-5 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
              <div className="flex items-center space-x-2">
                <select
                  value={searchField}
                  onChange={(e) => setSearchField(e.target.value as 'request' | 'bid')}
                  className="w-full sm:w-auto border rounded-md px-3 py-2"
                >
                  <option value="request">Request #</option>
                  <option value="bid">Bid #</option>
                </select>
                <input
                  type="text"
                  placeholder={`Search by ${searchField === 'request' ? 'request' : 'bid'} number...`}
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full sm:w-auto pl-10 pr-4 py-2 border rounded-md focus:ring-indigo-500 focus:border-indigo-500"
                />
              </div>
            </div>
            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value as any)}
              className="border rounded-md px-3 py-2"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="withdrawn">Withdrawn</option>
            </select>
          </div>
          <div className="overflow-x-auto -mx-4 sm:mx-0">
            <table className="min-w-full divide-y divide-gray-200">
              <thead>
                <tr>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Request Details
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Insurer
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Amount
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 bg-gray-50 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Time
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredBids.map((bid) => (
                  <tr key={`${bid.id}-${bid.request_id}-${bid.created_at}`}>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-900">Request #{bid.insurance_requests?.reference_number}</div>
                      <div className="text-sm text-gray-500">{bid.insurance_requests?.name}</div>
                      <div className="text-xs text-gray-500">Bid #{bid.reference_number}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {bid.insurers?.name}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      £{bid.amount.toLocaleString('en-GB', {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                        bid.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                      }`}>
                        {bid.status.charAt(0).toUpperCase() + bid.status.slice(1)}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                      {new Date(bid.created_at).toLocaleString('en-GB', {
                        day: '2-digit',
                        month: '2-digit',
                        year: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                      <button
                        onClick={() => setSelectedBid(bid)}
                        className="text-indigo-600 hover:text-indigo-900"
                      >
                        View Details
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {/* Bid Details Modal */}
      {selectedBid && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg max-w-2xl w-full p-6">
            <div className="flex justify-between items-center mb-4">
              <h3 className="text-lg font-medium text-gray-900">Bid Details</h3>
              <button
                onClick={() => setSelectedBid(null)}
                className="text-gray-400 hover:text-gray-500"
              >
                <span className="sr-only">Close</span>
                <XCircle className="h-6 w-6" />
              </button>
            </div>
            <div className="space-y-4">
              <div>
                <h4 className="text-sm font-medium text-gray-500">Request ID</h4>
                <p className="mt-1">{selectedBid.request_id}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Insurer</h4>
                <p className="mt-1">{selectedBid.insurer_name}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Amount</h4>
                <p className="mt-1">£{selectedBid.amount.toLocaleString('en-GB', {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2
                })}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Status</h4>
                <p className="mt-1">{selectedBid.status}</p>
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-500">Created At</h4>
                <p className="mt-1">{new Date(selectedBid.created_at).toLocaleString()}</p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
